/**
 * wsr-api notifications client (WSRPDM-0015, contract §4).
 *
 * Raw DTOs, no `unwrap()` — mirrors `./tenants.ts`/`./queue.ts`. Notifications
 * remain paged (`content`, size max 200); jobs list + journal (WSRPDM-0073)
 * are also paged (`content`, size max 100). The envelope shape
 * (`content`/`page`/`size`/`totalElements`/`totalPages`) matches the generic
 * `PagedResponse<T>` (`../types`) exactly, so it's reused rather than
 * re-declared.
 *
 * `status=all` is NOT a valid query value (contract §4.1) — omit the param
 * entirely for "all". `request()`'s `buildUrl` already skips `undefined`
 * query values (`../client.ts`), so passing `status: undefined` here is
 * sufficient — never pass the literal string `'all'`.
 */
import { request } from '../client';
import type { PagedResponse } from '../types';
import type { NotificationStatus, PlatformNotificationView } from './types';

const BASE = '/admin/platform/notifications';

export interface ListNotificationsQuery {
  // `| undefined` (not just `?`) because callers pass an explicit
  // `status: undefined` for the "all" tab — `exactOptionalPropertyTypes`
  // otherwise rejects that as distinct from omitting the key.
  status?: NotificationStatus | undefined;
  /** Unmatched ⇒ empty result (contract §4.1) — no client-side validation needed. */
  severity?: string | undefined;
  /** Exact slug, or `*` for platform-scope. Omit for all tenants. */
  tenant?: string | undefined;
  /** 0-based. Default 0. */
  page?: number;
  /** Default 20, max 200 (server-enforced). */
  size?: number;
}

/** GET /admin/platform/notifications (contract §4.1). */
export async function listNotifications(
  query: ListNotificationsQuery = {},
): Promise<PagedResponse<PlatformNotificationView>> {
  const { status, severity, tenant, page = 0, size = 20 } = query;
  return request<PagedResponse<PlatformNotificationView>>('GET', BASE, {
    query: { status, severity, tenant, page, size },
  });
}

/**
 * POST /admin/platform/notifications/{id}/acknowledge (contract §4.2). No
 * body — actor comes from the JWT. 200 empty body; OPEN → ACKNOWLEDGED only.
 * 404 `NOTIFICATION_NOT_FOUND` for an unknown id.
 */
export async function acknowledge(id: number): Promise<void> {
  await request<unknown>('POST', `${BASE}/${id}/acknowledge`);
}
